import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';

interface Kpis {
  candidatos_total: number;
  listas_total: number;
  designados_pendientes: number;
  listas_cuota_juventud_aplicable: number;
  listas_no_cumple_cuota_joven: number;
  mismatch_territorial_total: number;
  mismatch_baja: number;
  mismatch_media: number;
  mismatch_alta: number;
  paridad_violada_formulas: number;
  alternancia_violada_listas: number;
  desafiliados_jne: number;
  inscritos_jee: number;
  dias_para_cierre: number;
  padron_postulable_total: number;
  padron_postulable_rp: number;
  padron_postulable_perup: number;
  candidatos_no_en_padron_postulable: number;
}

interface Golden {
  candidatos_total: number;
  listas_total: number;
  designados_pendientes: number;
  mismatch_territorial_total: number;
  mismatch_baja: number;
  mismatch_media: number;
  mismatch_alta: number;
  desafiliados_jne_vigentes: number;
  alternancia_violada_listas: number;
  paridad_violada_formulas: number;
  listas_cuota_juventud_aplicable: number;
  listas_no_cumple_cuota_joven: number;
  designados_debe_ser_joven: number;
}

function KpiCard({ label, value, sub, intent, to }: { label: string; value: number | string; sub?: string; intent?: 'ok' | 'warn' | 'danger' | 'info'; to?: string }) {
  const tone = {
    ok: 'border-green-200 bg-green-50 hover:bg-green-100',
    warn: 'border-amber-200 bg-amber-50 hover:bg-amber-100',
    danger: 'border-red-200 bg-red-50 hover:bg-red-100',
    info: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
  }[intent ?? 'info'];
  const inner = (
    <div className={`border rounded p-4 transition ${tone} ${to ? 'cursor-pointer hover:shadow-md' : ''}`}>
      <div className="text-2xl font-bold text-gray-800">{typeof value === 'number' ? value.toLocaleString('es-PE') : value}</div>
      <div className="text-xs text-gray-700 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
      {to && <div className="text-[10px] text-arpp-primary mt-1 font-medium">Ver detalle →</div>}
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

export function Dashboard() {
  const { data: k, isLoading } = useQuery<Kpis>({
    queryKey: ['kpis'],
    queryFn: async () => (await api.get<Kpis>('/dashboard/kpis')).data,
  });
  const { data: g } = useQuery<Golden>({
    queryKey: ['golden'],
    queryFn: async () => (await api.get<Golden>('/dashboard/golden')).data,
  });

  if (isLoading || !k || !g) {
    return <div className="p-6 text-gray-500 text-sm">Cargando KPIs…</div>;
  }

  const okOrDelta = (actual: number, expected: number) => actual === expected ? '✅' : `⚠️ ${actual} (esperado ${expected})`;

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-arpp-primary">Dashboard global</h1>
          <p className="text-xs text-gray-600">Alianza Renovación Popular Perú · ERM 2026</p>
        </div>
        <div className={`px-4 py-2 rounded text-sm font-semibold ${
          k.dias_para_cierre <= 7 ? 'bg-red-100 text-red-800' :
          k.dias_para_cierre <= 21 ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
        }`}>
          ⏰ {k.dias_para_cierre} días al cierre JEE (16-jun-2026)
        </div>
      </header>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Escala</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label="Candidatos totales" value={k.candidatos_total} sub={`canónico: ${g.candidatos_total}`} intent={k.candidatos_total === g.candidatos_total ? 'ok' : 'danger'} to="/candidatos" />
          <KpiCard label="Listas totales" value={k.listas_total} sub={`canónico: ${g.listas_total}`} intent={k.listas_total === g.listas_total ? 'ok' : 'danger'} to="/listas" />
          <KpiCard label="Designados pendientes" value={k.designados_pendientes} sub={`canónico: ${g.designados_pendientes}`} intent={k.designados_pendientes === g.designados_pendientes ? 'warn' : 'danger'} to="/candidatos?es_designado=true" />
          <KpiCard label="Sin cuota joven" value={k.listas_no_cumple_cuota_joven} sub={`de ${k.listas_cuota_juventud_aplicable} aplicables`} intent="warn" to="/listas?cumple_cuota_joven=false" />
          <KpiCard label="Inscritos en JEE" value={k.inscritos_jee} sub={`de ${k.listas_total} listas`} intent={k.inscritos_jee === k.listas_total ? 'ok' : 'info'} to="/listas?estado_jee=inscrito" />
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Alertas bloqueantes (V01-V05, V09, V10)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Paridad H+M violada (V01)" value={k.paridad_violada_formulas} intent={k.paridad_violada_formulas === 0 ? 'ok' : 'danger'} to="/listas?cumple_paridad_horizontal=false" />
          <KpiCard label="Alternancia violada (V02-V04)" value={k.alternancia_violada_listas} intent={k.alternancia_violada_listas === 0 ? 'ok' : 'danger'} to="/listas?cumple_alternancia=false" />
          <KpiCard label="Desafiliados JNE (V10)" value={k.desafiliados_jne} intent={k.desafiliados_jne === 0 ? 'ok' : 'danger'} to="/candidatos?desafiliado_jne=true" />
          <KpiCard label="Mismatch territorial total (V11)" value={k.mismatch_territorial_total} intent={k.mismatch_territorial_total === 0 ? 'ok' : 'warn'} to="/candidatos?mismatch_territorial=true" />
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Domicilio múltiple — severidad</h2>
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label="BAJA (mismo dpto+prov, distinto distrito)" value={k.mismatch_baja} intent="info" to="/candidatos?severidad_mismatch=BAJA" />
          <KpiCard label="MEDIA (mismo dpto, otra provincia)" value={k.mismatch_media} intent="warn" to="/candidatos?severidad_mismatch=MEDIA" />
          <KpiCard label="ALTA (otra región)" value={k.mismatch_alta} intent="danger" to="/candidatos?severidad_mismatch=ALTA" />
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          Padrón Postulable DNROP-JNE (Of. 4172) — universo derecho a postular
        </h2>
        <p className="text-[11px] text-gray-500 mb-2">
          NO es el padrón de primarias. Aquí van todos los afiliados RP + PERUP elegibles
          para POSTULAR a cargos ERM2026 (excluye desafiliados). Un afiliado RP en Lima
          Provincias puede postular como ARPP en Lima Centro con domicilio múltiple.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total elegibles POSTULAR" value={k.padron_postulable_total} sub="canónico: 82,182" intent={k.padron_postulable_total === 82182 ? 'ok' : 'warn'} to="/padron?desafiliado=false" />
          <KpiCard label="RP elegibles" value={k.padron_postulable_rp} intent="info" to="/padron?op_canonica=RP&desafiliado=false" />
          <KpiCard label="PERUP elegibles" value={k.padron_postulable_perup} intent="info" to="/padron?op_canonica=PERUP&desafiliado=false" />
          <KpiCard label="Candidatos NO en padrón postulable (V09)" value={k.candidatos_no_en_padron_postulable} intent={k.candidatos_no_en_padron_postulable === 0 ? 'ok' : 'danger'} to="/candidatos?en_padron_postulable=false" />
        </div>
      </section>

      <section className="border rounded p-4 bg-white">
        <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-3">Golden test (cifras canónicas)</h2>
        <div className="text-xs font-mono grid grid-cols-2 gap-y-1 gap-x-6">
          <div>Total candidatos</div><div>{okOrDelta(k.candidatos_total, g.candidatos_total)}</div>
          <div>Listas únicas</div><div>{okOrDelta(k.listas_total, g.listas_total)}</div>
          <div>Designados pendientes</div><div>{okOrDelta(k.designados_pendientes, g.designados_pendientes)}</div>
          <div>Mismatch total</div><div>{okOrDelta(k.mismatch_territorial_total, g.mismatch_territorial_total)}</div>
          <div>Mismatch BAJA</div><div>{okOrDelta(k.mismatch_baja, g.mismatch_baja)}</div>
          <div>Mismatch MEDIA</div><div>{okOrDelta(k.mismatch_media, g.mismatch_media)}</div>
          <div>Mismatch ALTA</div><div>{okOrDelta(k.mismatch_alta, g.mismatch_alta)}</div>
          <div>Desafiliados JNE</div><div>{okOrDelta(k.desafiliados_jne, g.desafiliados_jne_vigentes)}</div>
        </div>
      </section>
    </div>
  );
}
